// THE LADDER (Matt, 2026-10-15). The eight principles were a puzzle you
// solved by April and then shopped from; the deep eight were "another
// scramble of the exact same puzzle". What he asked for instead: "go through
// the loop, solve the deduction puzzle, and then that becomes the foundation
// for the next deduction puzzle... over and over again".
//
// So every rung above the eight is a MACHINE the Stewards build (a Scholars'
// technology teaches them how). Feed it two things from the rung below, in
// order, and it makes one new thing - a glaze, an essence, an alloy - whose
// nature follows from its two parents' by the machine's hidden LAW, one rule
// per property ("hot if either is hot", "like the first one in"). The
// Scholars crack the law; a cracked law lets them make what a technology
// asks for without wasting firings. The products sit on the Scholars' SHELF
// and are what the next machine takes in, so a deep technology is a recipe
// tree, paid for in goods (the ingredients, as in Alchemists).
//
// Pure helpers over `civ.lore.machines = { kiln: { law: { heat, moisture,
// weight } } }`, `civ.lore.firings = { kiln: [{ a, b, out, round }] }` and
// `civ.lore.shelf = { kiln: { [natureKey]: n } }`.

import { nextInt } from './rng/rng';
import {
  ASPECTS, NATURES, natureKey, aspectWord, describeNature, PRINCIPLES, PRINCIPLE_NAMES,
} from './lore';

// One per rung above the eight, in order. A technology of tier N is made
// from what the machine of tier N makes. More rungs are more entries.
export const MACHINES = [
  {
    id: 'kiln', name: 'Kiln', tier: 3, tech: 'kilns', from: null,
    product: 'glaze', products: 'glazes', fuel: { goods: 2 },
    text: 'Fire two of the eight in it, one after the other, and a glaze comes out.',
  },
  {
    id: 'still', name: 'Still', tier: 4, tech: 'distilling', from: 'kiln',
    product: 'essence', products: 'essences', fuel: { goods: 1 },
    text: 'Run two glazes through it, one after the other, and an essence comes out.',
  },
  {
    id: 'crucible', name: 'Crucible', tier: 5, tech: 'assaying', from: 'still',
    product: 'alloy', products: 'alloys', fuel: { goods: 1 },
    text: 'Melt two essences in it, one after the other, and an alloy comes out.',
  },
];
export const MACHINE_IDS = MACHINES.map((m) => m.id);
export const machineById = (id) => MACHINES.find((m) => m.id === id) || null;
export const machineForTier = (tier) => MACHINES.find((m) => m.tier === tier) || null;
/** The highest tier the eight make directly; above it, a machine. */
export const BENCH_TIERS = 2;
export const onBench = (tier) => (tier || 1) <= BENCH_TIERS;

// ---------------------------------------------------------------- laws
//
// A law says, for ONE property, what comes out given what went in first
// and second. The menu is short and every entry is one plain sentence -
// Matt: "the possible laws of nature [must be] clear and they could get
// really weird". Later machines may also deal the later kinds.
export const LAW_KINDS = {
  either: { rung: 0 }, // "hot if either one is hot" (and the other way: "cold if either is cold")
  first: { rung: 0 }, // "like the first one in"
  second: { rung: 0 }, // "like the second one in"
  flip: { rung: 1 }, // "the opposite of the first one in"
  match: { rung: 2 }, // "hot if the two match, cold if they differ"
};
/** Every law a machine at this rung (0 = the kiln) may have, for one property. */
export const lawsFor = (rung, aspect) => {
  const out = [];
  Object.entries(LAW_KINDS).forEach(([kind, k]) => {
    if (k.rung > rung) return;
    if (kind === 'either' || kind === 'match') {
      out.push({ kind, aspect, value: true }, { kind, aspect, value: false });
    } else out.push({ kind, aspect });
  });
  return out;
};
export const rungOf = (machineId) => Math.max(0, MACHINE_IDS.indexOf(machineId));
export const lawKey = (law) => (law.value === undefined ? law.kind : `${law.kind}:${law.value}`);
export const lawFromKey = (aspect, key) => {
  const [kind, v] = key.split(':');
  return v === undefined ? { kind, aspect } : { kind, aspect, value: v === 'true' };
};

/** One property of the product, from the two that went in (first, second). */
export const applyLaw = (law, first, second) => {
  const a = first[law.aspect];
  const b = second[law.aspect];
  switch (law.kind) {
    case 'either': return a === law.value || b === law.value ? law.value : !law.value;
    case 'first': return a;
    case 'second': return b;
    case 'flip': return !a;
    case 'match': return a === b ? law.value : !law.value;
    default: return a;
  }
};
/** The product's whole nature under a machine's law. */
export const fireNatures = (laws, first, second) => Object.fromEntries(
  ASPECTS.map((k) => [k, applyLaw(laws[k], first, second)]),
);

/** The law as the board says it: "hot if either one in is hot". */
export const describeLaw = (law) => {
  const w = (v) => aspectWord(law.aspect, v);
  switch (law.kind) {
    case 'either': return `${w(law.value)} if either one in is ${w(law.value)}`;
    case 'first': return 'like the first one in';
    case 'second': return 'like the second one in';
    case 'flip': return 'the opposite of the first one in';
    case 'match': return `${w(law.value)} if the two match, ${w(!law.value)} if not`;
    default: return '?';
  }
};

/** The same rule, short enough for a chip on the law sheet: "hot if either". */
export const shortLaw = (law) => {
  const w = (v) => aspectWord(law.aspect, v);
  switch (law.kind) {
    case 'either': return `${w(law.value)} if either`;
    case 'first': return 'like 1st';
    case 'second': return 'like 2nd';
    case 'flip': return 'opposite of 1st';
    case 'match': return `${w(law.value)} if they match`;
    default: return '?';
  }
};

/**
 * Deal one machine's law: a rule per property from the menu for its rung.
 * Never the same KIND three times (a Kiln that copies the first one in is
 * no puzzle), and a later machine always has at least one rule that only
 * its rung allows, so climbing teaches something. Returns [laws, rng].
 */
export const dealLaw = (machineId, rng) => {
  const rung = rungOf(machineId);
  for (let tries = 0; tries < 50; tries += 1) {
    const laws = {};
    for (const k of ASPECTS) {
      const menu = lawsFor(rung, k);
      let i;
      [i, rng] = nextInt(menu.length, rng);
      laws[k] = menu[i];
    }
    const kinds = new Set(ASPECTS.map((k) => laws[k].kind));
    const fresh = rung === 0 || ASPECTS.some((k) => LAW_KINDS[laws[k].kind].rung === rung);
    if (kinds.size > 1 && fresh) return [laws, rng];
  }
  return [{ heat: { kind: 'either', aspect: 'heat', value: true }, moisture: { kind: 'first', aspect: 'moisture' }, weight: { kind: 'second', aspect: 'weight' } }, rng];
};

/** A save (or a new game) without machine laws is dealt them. Returns [civ, rng]. */
export const dealMissingMachines = (civ, rng) => {
  const lore = civ?.lore;
  if (!lore) return [civ, rng];
  const machines = { ...(lore.machines || {}) };
  let dealt = false;
  for (const m of MACHINES) {
    if (machines[m.id]?.law) continue;
    let law;
    [law, rng] = dealLaw(m.id, rng);
    machines[m.id] = { law };
    dealt = true;
  }
  if (!dealt) return [civ, rng];
  return [{ ...civ, lore: { ...lore, machines, firings: lore.firings || {}, shelf: lore.shelf || {} } }, rng];
};

// ---------------------------------------------------------------- the shelf
export const lawOf = (civ, machineId) => civ?.lore?.machines?.[machineId]?.law || null;
export const firingsOf = (civ, machineId) => civ?.lore?.firings?.[machineId] || [];
export const shelfOf = (civ, machineId) => civ?.lore?.shelf?.[machineId] || {};
export const onShelf = (civ, machineId, key) => shelfOf(civ, machineId)[key] || 0;
export const natureFromKey = (k) => NATURES.find((n) => natureKey(n) === k) || null;

/** What a machine takes in: the eight for the kiln, the rung below's products above it. */
export const inputsOf = (civ, machineId) => {
  const m = machineById(machineId);
  if (!m) return [];
  if (!m.from) return PRINCIPLES.slice();
  return Object.entries(shelfOf(civ, m.from)).filter(([, n]) => n > 0).map(([k]) => k);
};
/** Can these two go in, in this order? Two of the same kind need two on the shelf. */
export const canFeed = (civ, machineId, a, b) => {
  const m = machineById(machineId);
  if (!m || !a || !b) return false;
  if (!m.from) return PRINCIPLES.includes(a) && PRINCIPLES.includes(b);
  const shelf = shelfOf(civ, m.from);
  if (a === b) return (shelf[a] || 0) >= 2;
  return (shelf[a] || 0) >= 1 && (shelf[b] || 0) >= 1;
};
/** The nature of one input: a principle's hidden nature, or a product's (its key). */
export const inputNature = (civ, machineId, x) => {
  const m = machineById(machineId);
  if (!m?.from) return civ?.lore?.natures?.[x] || null;
  return natureFromKey(x);
};
/** How an input reads: "Fire", or "a hot, dry, heavy glaze". */
export const inputName = (machineId, x) => {
  const m = machineById(machineId);
  if (!m?.from) return PRINCIPLE_NAMES[x] || x;
  const below = machineById(m.from);
  const n = natureFromKey(x);
  return n ? `${describeNature(n)} ${below.product}` : x;
};
export const productName = (machineId, key) => {
  const m = machineById(machineId);
  const n = natureFromKey(key);
  return n && m ? `${describeNature(n)} ${m.product}` : key;
};

/** Does a product of this nature give what a technology needs? */
export const productFits = (key, need) => {
  const n = natureFromKey(key);
  return Boolean(n) && Object.entries(need || {}).every(([k, v]) => n[k] === v);
};
/** The first product on the shelf that a technology could be made from, or null. */
export const fittingProduct = (civ, techTier, need) => {
  const m = machineForTier(techTier);
  if (!m) return null;
  return Object.entries(shelfOf(civ, m.id)).find(([k, n]) => n > 0 && productFits(k, need))?.[0] || null;
};

/** The shelf after taking one away (or adding one, with +1). */
export const shelfAdd = (lore, machineId, key, n) => {
  const shelf = { ...(lore.shelf || {}) };
  const row = { ...(shelf[machineId] || {}) };
  row[key] = Math.max(0, (row[key] || 0) + n);
  if (!row[key]) delete row[key];
  shelf[machineId] = row;
  return { ...lore, shelf };
};

/**
 * Every law still consistent with the firings, per property - what a
 * careful notebook would have struck out by now. The phone does NOT show
 * this to players (the sheet is theirs to strike, like the eight's); the
 * bots reason with it, and the tests check the menu is crackable.
 * `natureOfInput(x)` says what each input is (null if not certain).
 */
export const lawsLeft = (machineId, firings, natureOfInput) => {
  const rung = rungOf(machineId);
  return Object.fromEntries(ASPECTS.map((k) => [k, lawsFor(rung, k).filter((law) => firings.every((f) => {
    const a = natureOfInput(f.a);
    const b = natureOfInput(f.b);
    if (!a || !b || a[k] === undefined || b[k] === undefined) return true;
    return applyLaw(law, a, b) === natureFromKey(f.out)[k];
  }))]));
};
